import type { SoapRegionPartState, SoapTreatmentPartState } from './part-finding.mapper';

export interface SoapObjectivePayload {
  /** Per-treatment PART state keyed by treatment GUID, synthetic id, or anatomy level name. */
  partDataPerLevelByLevelName?: Record<string, SoapTreatmentPartState | unknown>;
  /** Per noteRegion PART state — queue round-trip only (queue API has no regionNotes). */
  regionPartDataByNoteRegion?: Record<string, SoapRegionPartState>;
  soapObjectiveNotes?: string | null;
}

export function parseSoapObjectivePayload(
  raw: string | null | undefined,
): SoapObjectivePayload | null {
  if (!raw || !raw.trimStart().startsWith('{')) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as SoapObjectivePayload;
    if (
      parsed &&
      typeof parsed === 'object' &&
      (parsed.partDataPerLevelByLevelName || parsed.regionPartDataByNoteRegion)
    ) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
}

/** Case-note Objective is SOAP text only — never the PART JSON payload. */
export function unwrapSoapObjectiveNotes(raw: string | null | undefined): string | null {
  const payload = parseSoapObjectivePayload(raw);
  if (!payload) {
    return raw ?? null;
  }

  const text = payload.soapObjectiveNotes;
  if (text == null || String(text).trim() === '') {
    return null;
  }

  return String(text);
}

/** Wrap free-text objective notes with PART draft JSON for queue/offline round-trip. */
export function wrapSoapObjectivePayload(
  soapObjectiveNotes: string | null | undefined,
  partDataPerLevelByLevelName?: Record<string, unknown> | null,
  regionPartDataByNoteRegion?: Record<string, unknown> | null,
): string | null {
  const text = soapObjectiveNotes?.trim();
  const hasPartData =
    !!partDataPerLevelByLevelName && Object.keys(partDataPerLevelByLevelName).length > 0;
  const hasRegionData =
    !!regionPartDataByNoteRegion && Object.keys(regionPartDataByNoteRegion).length > 0;

  if (!text && !hasPartData && !hasRegionData) {
    return null;
  }

  const payload: SoapObjectivePayload = {
    soapObjectiveNotes: text ?? null,
    ...(hasPartData ? { partDataPerLevelByLevelName: partDataPerLevelByLevelName as SoapObjectivePayload['partDataPerLevelByLevelName'] } : {}),
    ...(hasRegionData ? { regionPartDataByNoteRegion: regionPartDataByNoteRegion as SoapObjectivePayload['regionPartDataByNoteRegion'] } : {}),
  };

  return JSON.stringify(payload);
}
